import React from 'react';
import { AlertTriangle, TrendingUp, Calendar, CheckCircle } from 'lucide-react';

const PredictiveInsights = ({ predictions }) => {
  const getRiskIcon = (risk) => {
    switch (risk) {
      case 'high':
        return <AlertTriangle className="w-6 h-6 text-red-500" />;
      case 'medium':
        return <TrendingUp className="w-6 h-6 text-yellow-500" />;
      case 'low':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      default:
        return <Calendar className="w-6 h-6 text-blue-500" />;
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'high':
        return 'bg-red-50 border-red-200';
      case 'medium':
        return 'bg-yellow-50 border-yellow-200';
      case 'low':
        return 'bg-green-50 border-green-200';
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  const getRiskBadge = (risk) => {
    switch (risk) {
      case 'high':
        return 'bg-red-100 text-red-700';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'low':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-blue-100 text-blue-700';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-orange-100 rounded-lg">
          <AlertTriangle className="w-6 h-6 text-orange-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Predictive Insights</h2>
          <p className="text-sm text-gray-600">AI-powered predictions based on your patterns</p>
        </div>
      </div>

      <div className="space-y-4">
        {predictions?.map((prediction, index) => (
          <div
            key={index}
            className={`border rounded-lg p-5 ${getRiskColor(prediction?.risk)}`}
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-1">
                {getRiskIcon(prediction?.risk)}
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-gray-900 text-lg">{prediction?.message}</h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRiskBadge(prediction?.risk)}`}>
                    {prediction?.risk?.toUpperCase()} RISK
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <div className="text-sm text-gray-600">Confidence Level</div>
                    <div className="mt-1">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${prediction?.confidence * 100}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-900">
                          {(prediction?.confidence * 100)?.toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Timeframe</div>
                    <div className="text-sm font-medium text-gray-900 mt-1">{prediction?.timeframe}</div>
                  </div>
                </div>

                {prediction?.recommendation && (
                  <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">Recommended Action</div>
                        <div className="text-sm text-gray-700 mt-1">{prediction?.recommendation}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {(!predictions || predictions?.length === 0) && (
          <div className="text-center py-8">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <p className="text-gray-600">No concerning patterns detected</p>
            <p className="text-sm text-gray-500 mt-1">Your symptoms appear stable</p>
          </div>
        )}
      </div>

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-900">
            <strong>Important:</strong> These predictions are based on pattern analysis and should not replace
            professional medical advice. Always consult your healthcare provider for medical decisions.
          </div>
        </div>
      </div>
    </div>
  );
};

export default PredictiveInsights;